import React, { useEffect, useState } from 'react';
import { motion } from 'framer-motion';
import { Link } from 'react-router-dom';
import { ArrowRight, Code2, FileCode2, ServerCog } from 'lucide-react';
import SEO from '../components/SEO';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const BLOCKS = [
  { id: 'api', icon: Code2, title: 'API y endpoints', copy: 'Base FastAPI con rutas para autenticación, pagos, contratos, operación y paneles.' },
  { id: 'mcp', icon: ServerCog, title: 'MCP Integration Guide', copy: 'Neuralia ya prepara una capa MCP nativa para discovery de herramientas y routing controlado.' },
  { id: 'sdks', icon: FileCode2, title: 'SDKs y ejemplos', copy: 'Python y JavaScript entran primero. PHP y ejemplos ampliados pueden venir en la siguiente fase.' },
];

export default function DeveloperPage() {
  const [portal, setPortal] = useState(null);

  useEffect(() => {
    const loadPortal = async () => {
      const response = await fetch(`${API}/developer/portal`);
      const data = await response.json();
      if (response.ok) setPortal(data);
    };
    loadPortal();
  }, []);

  return (
    <>
      <SEO title="Developer | Neuralia369" description="Punto de entrada para API, documentación técnica y guía MCP de Neuralia369." path="/developer" />
      <main className="min-h-screen bg-background px-4 py-16 sm:px-6 lg:px-8" data-testid="developer-page">
        <div className="mx-auto max-w-7xl space-y-10">
          <motion.section initial={{ opacity: 0, y: 18 }} animate={{ opacity: 1, y: 0 }} className="glass-card tracing-panel p-8 md:p-10" data-testid="developer-hero-card">
            <p className="editorial-kicker mb-4">Developer</p>
            <h1 className="text-4xl sm:text-5xl lg:text-6xl text-text-heading">Documentación técnica, API y guía MCP.</h1>
            <p className="mt-4 max-w-3xl text-lg text-text-body">Esta ruta concentra la parte técnica pública sin exponer la estrategia interna. Sirve como base para docs, Swagger, SDKs y el mapa de integración MCP.</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link to="/contacto" className="btn-primary" data-testid="developer-primary-cta">Pedir acceso técnico <ArrowRight className="w-4 h-4" /></Link>
              <Link to="/seguridad" className="btn-secondary" data-testid="developer-secondary-cta">Ver seguridad</Link>
            </div>
          </motion.section>

          <section className="grid gap-5 md:grid-cols-3" data-testid="developer-blocks-grid">
            {BLOCKS.map(({ id, icon: Icon, title, copy }) => (
              <article key={id} className="glass-card p-6" data-testid={`developer-block-${id}`}>
                <div className="rounded-2xl bg-primary/10 p-3 text-primary w-fit"><Icon className="w-5 h-5" /></div>
                <h2 className="mt-4 text-xl text-text-heading">{title}</h2>
                <p className="mt-3 text-sm text-text-body">{copy}</p>
              </article>
            ))}
          </section>

          {portal && (
            <section className="grid gap-5 xl:grid-cols-[0.9fr_1.1fr]" data-testid="developer-live-portal-grid">
              <article className="glass-card p-6" data-testid="developer-live-links-card">
                <p className="editorial-kicker mb-4">Rutas reales</p>
                <div className="space-y-3 text-sm text-text-body">
                  <a href={portal.openapi_json_url} className="block hover:text-primary" data-testid="developer-openapi-link">OpenAPI JSON</a>
                  <a href={portal.mcp_server_url} className="block hover:text-primary" data-testid="developer-mcp-link">Servidor MCP local</a>
                  <a href={portal.developer_page_url} className="block hover:text-primary" data-testid="developer-self-link">Portal developer</a>
                </div>
              </article>
              <article className="glass-card p-6" data-testid="developer-playground-card">
                <p className="editorial-kicker mb-4">Playground técnico</p>
                <div className="space-y-3">
                  {portal.playground_routes?.map((route) => (
                    <div key={route} className="rounded-[18px] border border-border-subtle bg-background/45 p-3 text-sm text-text-body" data-testid={`developer-route-${route.replace(/[^a-zA-Z0-9]/g, '-')}`}>{route}</div>
                  ))}
                </div>
              </article>
            </section>
          )}
        </div>
      </main>
    </>
  );
}